import { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../styles/CreateTerminalPage.css";
import {
  FaStore,
  FaCreditCard,
  FaLocationDot,
  FaFingerprint,
  FaUserCheck,
  FaWifi,
  FaLayerGroup,
  FaBarcode,
  FaServer ,
  FaKeyboard,
} from "react-icons/fa6";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { createTerminal, getAllMerchants, updateTerminal } from "../api/api";
import { POSTERMINAL } from "../utils/constants";
import { toDropdownOptions } from "../utils/enumUtils";

// ✅ Validation Schema
const validationSchema = Yup.object().shape({
  merchantId: Yup.string().required("Merchant ID is required"),
  terminalId: Yup.string()
    .matches(/^[0-9]{1,8}$/, "Terminal ID must be 1–8 digits")
    .min(8, "Terminal ID cannot less then 8 digits")
    .required("Terminal ID is required"),
  phoneNumber: Yup.string()
    .test("is-valid-phone", "Enter a valid phone number", (value) => {
      return value && value.replace(/\D/g, "").length > 4;
    })
    .required("Phone number is required"),
  attended: Yup.string().required("Attended type is required"),
  cardholderActivated: Yup.string().required(
    "Cardholder activation is required"
  ),
  cardPresenceCapability: Yup.string().required(
    "Card presence capability is required"
  ),
  inputCapability: Yup.string().required("Input capability is required"),
  location: Yup.string().required("Location is required"),
  onlineReasonCode: Yup.string().required("Online reason code is required"),
  panEntryMode: Yup.string().required("PAN entry mode is required"),
  pinEntryCapability: Yup.string().required("PIN entry capability is required"),
  environment: Yup.string()
    .oneOf(["TEST", "PRODUCTION"], "Invalid environment")
    .required("Environment is required"),

  transactionType: Yup.array()
    .of(
      Yup.string().oneOf(
        [
          "SALES",
          "VOID",
          "REFUND",
          "SETTLEMENT",
          "PRE AUTH",
          "PRE AUTH COMPLETION",
          "TIP",
          "TIP COMPLETION",
          "SALES ADJUST",
          "BALANCE INQUIRY",
          "QR PAYMENTS","KISAN CARD"
        ],
        "Invalid Card"
      )
    )
    .min(1, "At least one Transaction Type is required")
    .required("Transaction Type is required"),
  lane: Yup.string().required("Lane is required"),
  cards: Yup.array()
    .of(
      Yup.string().oneOf(
        ["VC", "MC", "JCB", "UPI", "PURE", "DIN"],
        "Invalid Card"
      )
    )
    .min(1, "At least one card is required")
    .required("Card is required"),
  // serialNumber: Yup.string().required("Serial number is required"),
});

// ✅ Default Form Values
const INITIAL_FORM = {
  merchantId: "",
  terminalId: "",
  phoneNumber: "92",
  attended: "",
  cardholderActivated: "",
  cardPresenceCapability: "",
  inputCapability: "",
  location: "",
  onlineReasonCode: "",
  panEntryMode: "",
  pinEntryCapability: "",
  transactionType: [],
  environment: "",

  lane: "",
  cards: [],
  // serialNumber: "",
};
const CARD_OPTIONS = [
  { label: "Visa Card", value: "VC" },
  { label: "Master Card", value: "MC" },
  { label: "Pay Pak", value: "PURE" },
  { label: "Union Pay", value: "UPI" },
  { label: "Japenese Credit Bureau", value: "JCB" },
  { label: "Diners", value: "DIN" },
];
const ENVIRONMENT_OPTIONS = [
  { label: "Test", value: "TEST" },
  { label: "Production", value: "PRODUCTION" },
];

export default function CreateTerminalPage() {
  const [loading, setLoading] = useState(false);
  const [merchantLoading, setMerchantLoading] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Extract terminal and mode from route state
  const { terminal, mode } = location.state || {};
  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  // ✅ Fetch merchants for dropdown
  useEffect(() => {
    const fetchData = async () => {
      try {
        setMerchantLoading(true);
        const res = await getAllMerchants();
        if (res?.data) setMerchants(res.data);
      } catch (err) {
        console.error("Error fetching merchants:", err);
      } finally {
        setMerchantLoading(false);
      }
    };
    fetchData();
  }, []);

  // ✅ Handle submit (create or update)
  const handleSubmit = async (values, { resetForm }) => {
    if (isViewMode) return;
    // setLoading(true);
    try {
      console.log("values", JSON.stringify(values, null, 2));
      const res = isEditMode
        ? await updateTerminal(values)
        : await createTerminal(values);

      if (res?.isSuccess) {
        alert(
          isEditMode
            ? "Terminal updated successfully!"
            : "Terminal created successfully!"
        );

        navigate("/dashboard/terminalslist");
      } else {
        alert(res?.message || "Failed to save terminal!");
      }
    } catch (err) {
      console.error("Terminal Save Error:", err);
      alert(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Initial values based on mode
  const formInitialValues =
    isEditMode || isViewMode
      ? {
          ...INITIAL_FORM,
          merchantId: terminal?.merchantId || terminal?.MID || "",
          terminalId: terminal?.TID || "",
          phoneNumber: terminal?.PhoneNumber || "92",
          attended: terminal?.attended || "",
          cardholderActivated: terminal?.cardholderActivated || "",
          cardPresenceCapability: terminal?.cardPresenceCapability || "",
          inputCapability: terminal?.inputCapability || "",
          location: terminal?.location || "",
          onlineReasonCode: terminal?.onlineReasonCode || "",
          panEntryMode: terminal?.panEntryMode || "",
          pinEntryCapability: terminal?.pinEntryCapability || "",
          transactionType: terminal?.transactionType || "",
          environment: terminal?.environment || "",

          lane: terminal?.lane || "",
          cards: terminal?.cards || [],
          // serialNumber: terminal?.serialNumber || "",
        }
      : INITIAL_FORM;

  if (merchantLoading) {
    return (
      <div className="create-terminal-page">
        <p className="loader">Loading...</p>
      </div>
    );
  }

  return (
    <div className="create-terminal-page">
      <div className="create-terminal-container">
        <Formik
          initialValues={formInitialValues}
          validationSchema={isViewMode ? null : validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form className="create-terminal-form">
              {/* Merchant ID */}
              <div className="input-with-icon">
                <FaStore className="input-icon" />
                <Dropdown
                  name="merchantId"
                  value={values.merchantId}
                  options={merchants.map((m) => ({
                    label: m.MID,
                    value: m.MID,
                  }))}
                  disabled={isViewMode || isEditMode}
                  onChange={(e) => setFieldValue("merchantId", e.value)}
                  placeholder="Select Merchant"
                  className="onboard-input w-full"
                  filter
                  showClear
                />
              </div>
              <ErrorMessage
                name="merchantId"
                component="div"
                className="error-text"
              />

              {/* Terminal ID */}
              <div className="input-with-icon">
                <FaCreditCard className="input-icon" />
                <Field
                  name="terminalId"
                  as={InputText}
                  maxLength={8}
                  placeholder="Terminal ID"
                  className="onboard-input"
                  disabled={isViewMode || isEditMode}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, "");
                    setFieldValue("terminalId", onlyDigits);
                  }}
                />
              </div>
              <ErrorMessage
                name="terminalId"
                component="div"
                className="error-text"
              />

              {/* Phone Number */}
              <div className="input-with-icon">
                <PhoneInput
                  country="pk"
                  value={values.phoneNumber}
                  onChange={(value) =>
                    setFieldValue("phoneNumber", value.replace(/\D/g, ""))
                  }
                  placeholder="Enter phone number"
                  containerClass="create-terminal__phone"
                  inputClass="create-terminal__phone-input"
                  enableSearch
                  countryCodeEditable={false}
                  disabled={isViewMode || isEditMode}
                />
              </div>
              <ErrorMessage
                name="phoneNumber"
                component="div"
                className="error-text"
              />

              {/* Dynamic Dropdowns */}
              {[
                {
                  name: "attended",
                  label: "Attended",
                  icon: FaUserCheck,
                  options: POSTERMINAL.ATTENDED,
                },
                {
                  name: "cardholderActivated",
                  label: "Cardholder Activated",
                  icon: FaFingerprint,
                  options: POSTERMINAL.CARDHOLDER_ACTIVATED,
                },
                {
                  name: "cardPresenceCapability",
                  label: "Card Presence Capability",
                  icon: FaCreditCard,
                  options: POSTERMINAL.CARD_PRESENCE_CAPABILITY,
                },
                {
                  name: "inputCapability",
                  label: "Input Capability",
                  icon: FaKeyboard,
                  options: POSTERMINAL.INPUT_CAPABILITY,
                },
                {
                  name: "location",
                  label: "Location",
                  icon: FaLocationDot,
                  options: POSTERMINAL.LOCATION,
                },
                {
                  name: "onlineReasonCode",
                  label: "Online Reason Code",
                  icon: FaWifi,
                  options: POSTERMINAL.ONLINE_REASON_CODE,
                },
                {
                  name: "panEntryMode",
                  label: "PAN Entry Mode",
                  icon: FaCreditCard,
                  options: POSTERMINAL.PAN_ENTRY_MODE,
                },
                {
                  name: "pinEntryCapability",
                  label: "PIN Entry Capability",
                  icon: FaKeyboard,
                  options: POSTERMINAL.PIN_ENTRY_CAPABILITY,
                },
              ].map((field) => (
                <>
                  <div className="input-with-icon">
                    <field.icon className="input-icon" />
                    <Dropdown
                      name={field.name}
                      value={values[field.name]}
                      options={toDropdownOptions(field.options)}
                      onChange={(e) => setFieldValue(field.name, e.value)}
                      placeholder={field.label}
                      className="onboard-input w-full"
                      disabled={isViewMode}
                      filter
                      showClear
                    />
                  </div>
                  <ErrorMessage
                    name={field.name}
                    component="div"
                    className="error-text"
                  />
                </>
              ))}
              {/* Environment */}
              <div className="input-with-icon">
                <FaServer  className="input-icon" />
                <Dropdown
                  name="environment"
                  value={values.environment}
                  options={ENVIRONMENT_OPTIONS}
                  onChange={(e) => setFieldValue("environment", e.value)}
                  placeholder="Select Environment"
                  className="onboard-input w-full"
                  disabled={isViewMode}
                  filter
                  showClear
                />
                
              </div>
              <ErrorMessage
                name="environment"
                component="div"
                className="error-text"
              />

              {/* Lane */}
              <div className="input-with-icon">
                <FaLayerGroup className="input-icon" />
                <Field
                  name="lane"
                  as={InputText}
                  placeholder="Lane"
                  className="onboard-input"
                  disabled={isViewMode}
                />
              </div>
              <ErrorMessage
                name="lane"
                component="div"
                className="error-text"
              />

              <div className="roles-section">
                <div className="roles-header">
                  <FaCreditCard className="input-icon" />
                  <label className="roles-label"> Cards Scheme</label>
                </div>
                <div className="roles-list">
                  {CARD_OPTIONS.map((card) => (
                    <label key={card.value} className="role-checkbox">
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        name="roles"
                        value={card.value}
                        checked={values.cards.includes(card.value)}
                        disabled={isViewMode}
                        onChange={(e) => {
                          const isChecked = e.target.checked;

                          let updatedCards = [...values.cards];

                          if (isChecked) {
                            updatedCards.push(card.value);
                          } else {
                            updatedCards = updatedCards.filter(
                              (val) => val !== card.value
                            );
                          }

                          setFieldValue("cards", updatedCards);
                        }}
                      />
                      <span>{card.label}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage
                  name="cards"
                  component="div"
                  className="error-text"
                />
              </div>
              <div className="roles-section">
                <div className="roles-header">
                  <FaCreditCard className="input-icon" />
                  <label className="roles-label"> Transaction Type</label>
                </div>
                <div className="roles-list">
                  {POSTERMINAL.TRANSACTION_TYPE.map((type) => (
                    <label key={type.value} className="role-checkbox">
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        name="transactionType"
                        value={type.value}
                        checked={values.transactionType.includes(type.value)}
                        disabled={isViewMode}
                        onChange={(e) => {
                          const isChecked = e.target.checked;

                          let updatedCards = [...values.transactionType];

                          if (isChecked) {
                            updatedCards.push(type.value);
                          } else {
                            updatedCards = updatedCards.filter(
                              (val) => val !== type.value
                            );
                          }

                          setFieldValue("transactionType", updatedCards);
                        }}
                      />
                      <span>{type.label}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage
                  name="transactionType"
                  component="div"
                  className="error-text"
                />
              </div>

              {/* Buttons */}

              {!isViewMode && (
                <Button
                  type="submit"
                  label={
                    loading ? "Saving..." : isEditMode ? "Update" : "Create"
                  }
                  disabled={loading}
                  className="create-terminal__submit"
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
